/**
 * @file components/AuthorProfile/component.js
 * @copyright 2017 CBC/Radio-Canada. All rights reserved.
 */

import PropTypes from "prop-types";
import ImageWrapper from "../../../../components/ImageWrapper/component";
import Title from "../../../../components/Title/component";
import LinkWrap from "../../../../components/LinkWrap/component";
import {
    AUTHOR_IMAGE_RATIO,
    AUTHOR_PROFILE_PAGE_IMAGE_SIZE,
    AUTHOR_PROFILE_PAGE_IMAGE_DERIVATIVE_WIDTHS,
    DETAIL_PAGE_AUTHOR_IMAGE_SIZE,
    DETAIL_PAGE_AUTHOR_IMAGE_DERIVATIVE_WIDTHS,
} from "./constants";
import {
    isAbsoluteUrl,
    removeUrlDomain,
} from "../../../../utils/path/library.js";

/**
 * Author Profile for the story page
 */
export function AuthorProfile({
    json = {},
    heading = "ABOUT THE AUTHOR",
    authorProfilePage = false,
}) {
    const dataItems = json;
    const photoURL = dataItems?.photoURL || "";

    const sizes = authorProfilePage
        ? AUTHOR_PROFILE_PAGE_IMAGE_SIZE
        : DETAIL_PAGE_AUTHOR_IMAGE_SIZE;

    const derivativeWidths = authorProfilePage
        ? AUTHOR_PROFILE_PAGE_IMAGE_DERIVATIVE_WIDTHS
        : DETAIL_PAGE_AUTHOR_IMAGE_DERIVATIVE_WIDTHS;

    const AuthorImage = photoURL ? (
        <div
            className="authorprofile-image-container"
            data-testid="author-profile-image-container"
        >
            <ImageWrapper
                url={photoURL}
                derivativeWidths={derivativeWidths}
                sizesRatio={AUTHOR_IMAGE_RATIO}
                sizes={sizes}
                type="author-image"
            />
        </div>
    ) : null;

    // Adding more checks, no point on showing profile without these set
    if (dataItems && dataItems.name) {
        // Author Profile on Bio Page
        if (authorProfilePage) {
            return (
                <div
                    className="authorprofile"
                    data-testid="author-profile"
                    data-cy="author-profile"
                >
                    <div className="authorprofile-container">
                        {AuthorImage}

                        <div className="authorprofile-name-container">
                            <p className="authorprofile-name">
                                {dataItems.name}
                            </p>
                            <p className="authorprofile-title">
                                {dataItems.title}
                            </p>
                            <p className="authorprofile-biography">
                                {dataItems.biography}
                            </p>
                            {getAuthorLinkList(dataItems.links)}
                        </div>
                    </div>
                </div>
            );
        }

        // Summarize Author Profile at the bottom of the Story
        if (dataItems.biography) {
            const name = dataItems.url ? (
                <LinkWrap url={dataItems.url}>{dataItems.name}</LinkWrap>
            ) : (
                <p className="authorprofile-name">{dataItems.name}</p>
            );

            return (
                <div className="authorprofile" data-testid="author-profile">
                    <Title level="h2">{heading}</Title>

                    <div className="authorprofile-container">
                        {AuthorImage}

                        <div
                            className="authorprofile-name-container"
                            data-testid="author-profile-name-container"
                        >
                            {name}
                            <p className="authorprofile-title">
                                {dataItems.title}
                            </p>
                        </div>
                    </div>

                    <p className="authorprofile-biography">
                        {dataItems.biography}
                    </p>
                    {getAuthorLinkList(dataItems.links)}
                </div>
            );
        }
    }

    return null;
}

function getAuthorLinkList(links) {
    const linkList = authorLinks(links);

    return <ul className="authorprofile-links">{linkList}</ul>;
}

function authorLinks(links) {
    if (!links || !links.length) return;

    return links.reduce((linkList, link) => {
        // Make relative URL for internal
        const url = removeUrlDomain(link.url);
        const external = !!isAbsoluteUrl(url);

        link.type === "link" &&
            linkList.push(
                <li
                    key={`${link.title}-${url}`}
                    className="authorprofile-linkitem"
                    data-testid="author-profile-linkitem"
                >
                    <LinkWrap
                        url={url}
                        external={external}
                        className="authorprofile-item"
                    >
                        {link.title}
                    </LinkWrap>
                </li>
            );

        return linkList;
    }, []);
}

// json: author profile data, contains title, image, description, name, etc
// heading: heading intro using default headline styles
// authorProfilePage: markup change depending on this component being set on a profile page or not

AuthorProfile.propTypes = {
    json: PropTypes.object.isRequired,
    heading: PropTypes.string,
    authorProfilePage: PropTypes.bool,
};

export default AuthorProfile;
